import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ApiCommonService } from '@api-services/api-common.service';
import { COMPANY_API_ROUTE_BASE } from '@constants/api-routes.constant';
import { ApiAlertError } from '@decorators/api-alert-error';
import { HttpHelper } from '@helpers/http.helper';
import { BalanceSharedFilters } from '@interfaces/balance-shared-filters';
import { AccountingPeriod } from '@models/accounting-period';
import { ImportPcgEntriesParameters } from '@models/import-pcg-entries-parameters';
import { PcgEntry, PcgEntryJson } from '@models/pcg-entry';
import { TransferCheck } from '@models/transfer-check';

export interface PcgEntryFilters extends BalanceSharedFilters {
  accountingPeriod?: AccountingPeriod;
  accountNumber?: string;
}

/**
 * Pcg Entry Service
 */
@Injectable({
  providedIn: 'root'
})
export class PcgEntryApiService extends ApiCommonService<PcgEntry> {
  resourceUrl = `${COMPANY_API_ROUTE_BASE}/pcg_entries`;

  fromJson(json: PcgEntryJson): PcgEntry {
    return PcgEntry.fromJson(json);
  }

  toJson(model: PcgEntry): Partial<PcgEntryJson> {
    return PcgEntry.toJson(model);
  }

  /**
   *
   * @param expand Expand
   * @param filters Filtre à appliquer
   */
  @ApiAlertError()
  getPcgEntries(filters?: PcgEntryFilters, companyId?: number): Observable<PcgEntry[]> {
    const url: string = !companyId ? this.resourceUrl : HttpHelper.replaceCompanyUrl(this.resourceUrl, companyId);
    let params: HttpParams;
    if (filters) {
      params = HttpHelper.setParams({
        accounting_period: filters?.accountingPeriod?.id ?? undefined,
        pcg_entry_balanced: filters?.pcgEntryBalanced === 'all' ? undefined : filters.pcgEntryBalanced,
        pcg_entry_has_movement: filters?.pcgEntryHasMovement === 'all' ? undefined : filters.pcgEntryHasMovement,
        pcg_entry_validated: filters?.pcgEntryValidated === 'all' ? undefined : filters.pcgEntryValidated,
        account_number: filters?.accountNumber ?? undefined
      });
    }
    return super.getAll({ params }, url);
  }

  getDefaultPcgEntry(): Observable<PcgEntry> {
    return this.http.get(`${this.resourceUrl}/default`).pipe(tap((json: any) => this.fromJson(json)));
  }

  @ApiAlertError()
  createPcgEntry(pcgEntry: PcgEntry): Observable<PcgEntry> {
    return super.create(pcgEntry);
  }

  @ApiAlertError()
  updatePcgEntry(pcgEntry: PcgEntry): Observable<PcgEntry> {
    return super.update(pcgEntry.id, pcgEntry);
  }

  @ApiAlertError()
  updatePcgEntries(pcgEntries: PcgEntry[]): Observable<PcgEntry[]> {
    return super.updateMultiple(pcgEntries, undefined, 'PATCH');
  }

  @ApiAlertError()
  removePcgEntry(pcgEntryId: number): Observable<unknown> {
    return super.delete(pcgEntryId);
  }

  @ApiAlertError()
  importPcgEntries(file: File, importPcgEntriesParameters: ImportPcgEntriesParameters): Observable<any> {
    const url = `${this.resourceUrl}/imports`;
    const formData = new FormData();
    const importPcgEntriesParametersJson = ImportPcgEntriesParameters.toJson(importPcgEntriesParameters);

    Object.entries(importPcgEntriesParametersJson).forEach(([key, value]) => formData.append(key, value as string));
    formData.append('file', file);

    return this.http.post(url, formData);
  }

  /**
   * Check if we can change label when we want to transfer movements from account to account.
   *
   * @returns the checked status
   */
  @ApiAlertError()
  transferCheck(pcgEntryId: number, accountingPeriodId: number): Observable<TransferCheck> {
    const url = `${this.resourceUrl}/${pcgEntryId}/transfer_movements/check`;

    let params: HttpParams = HttpHelper.createHttpParams();
    if (accountingPeriodId) {
      params = HttpHelper.setParam('accounting_period', accountingPeriodId.toString(), params);
    }

    return this.http
      .post<void>(url, null, { params })
      .pipe(map((transferCheckJson: any) => TransferCheck.fromJson(transferCheckJson)));
  }

  @ApiAlertError()
  archivePcgEntries(pcgEntries: PcgEntry[], archive: boolean): Observable<PcgEntry[]> {
    const body = pcgEntries.map((pcgEntry: PcgEntry) => ({ id: pcgEntry.id, archived: archive }));

    return this.http
      .patch(this.resourceUrl, body)
      .pipe(map((pcgEntriesJson: PcgEntryJson[]) => pcgEntriesJson.map(pcgEntryJson => this.fromJson(pcgEntryJson))));
  }

  @ApiAlertError()
  lowercaseSelectedPcgEntriesLabels(pcgEntries: PcgEntry[]): Observable<PcgEntry[]> {
    const body = pcgEntries.map((pcgEntry: PcgEntry) => ({
      id: pcgEntry.id,
      label: pcgEntry.label.charAt(0).toUpperCase() + pcgEntry.label.slice(1).toLowerCase()
    }));

    return this.http
      .patch(this.resourceUrl, body)
      .pipe(map((pcgEntriesJson: PcgEntryJson[]) => pcgEntriesJson.map(pcgEntryJson => this.fromJson(pcgEntryJson))));
  }

  @ApiAlertError()
  uppercaseSelectedPcgEntriesLabels(pcgEntries: PcgEntry[]): Observable<PcgEntry[]> {
    const body = pcgEntries.map((pcgEntry: PcgEntry) => ({
      id: pcgEntry.id,
      label: pcgEntry.label.toUpperCase()
    }));

    return this.http
      .patch(this.resourceUrl, body)
      .pipe(map((pcgEntriesJson: PcgEntryJson[]) => pcgEntriesJson.map(pcgEntryJson => this.fromJson(pcgEntryJson))));
  }

  @ApiAlertError()
  archivePcgEntry(pcgEntryId: number, archive: boolean): Observable<PcgEntry> {
    const url = `${this.resourceUrl}/${pcgEntryId}`;
    const body = {
      id: pcgEntryId,
      archived: archive
    };

    return this.http.patch(url, body).pipe(map((pcgEntryJson: PcgEntryJson) => this.fromJson(pcgEntryJson)));
  }

  /**
   * Récupère la liste des comptes sélectionnable pour générer les "à nouveaux" lors de la clôture d'un exercice
   *
   * @param {number} companyId
   * @returns {Observable<PcgEntry[]>}
   */
  @ApiAlertError()
  getANouveauxPcgEntries(accountingPeriodId: number, companyId?: number): Observable<PcgEntry[]> {
    let url: string = `${COMPANY_API_ROUTE_BASE}/accounting_periods/${accountingPeriodId}/a_nouveaux_pcg_entries`;
    if (companyId) {
      url = HttpHelper.replaceCompanyUrl(url, companyId);
    }
    return this.http
      .get<PcgEntryJson[]>(url)
      .pipe(
        map((pcgEntriesJson: PcgEntryJson[]) =>
          pcgEntriesJson.map((pcgEntryJson: PcgEntryJson) => this.fromJson(pcgEntryJson))
        )
      );
  }
}
